import { StanButton, StanDropdownMenu } from '@/components';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import type { DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';
import { createIconElement } from '@/lib/utils/createIconElement';
import { EXTERNAL_LINKS } from '@/lib/constants/externalLinks';
import IconPaypal from '@/assets/icons/payments/paypal.svg';
import IcMoreVertical from '@/assets/icons/ic-more-vertical.svg';

interface PayPalStatus {
    integrationId?: number;
    isRestricted: boolean;
    isRestrictedDueToEmail: boolean;
}

interface PayPalPaymentCardProps {
    paypal: PayPalStatus;
    loading: boolean;
    onDisconnect: (integrationId: number) => void;
    onHelpDoc: () => void;
}

export const PayPalPaymentCard = ({ paypal, loading, onDisconnect, onHelpDoc }: PayPalPaymentCardProps) => {
    const handleManage = () => {
        window.open(EXTERNAL_LINKS.PAYPAL_MANAGE, '_blank');
    };

    const handleMenuSelect = (value: DropdownValue) => {
        if (value.value === 'disconnect' && paypal.integrationId) {
            onDisconnect(paypal.integrationId);
        }
    };

    return (
        <div className='flex flex-col gap-2'>
            <div className='bg-bg-subtle rounded-lg p-4'>
                <div className='flex items-center justify-between'>
                    <div className='flex h-10 w-24 items-center'>
                        {createIconElement(IconPaypal, { removeFill: false })}
                    </div>
                    <div className='flex items-center gap-2'>
                        <StanButton
                            name='paypal-connect-manage-button'
                            variant='outline'
                            size='sm'
                            onClick={handleManage}
                            disabled={loading}
                        >
                            Manage
                        </StanButton>
                        <DropdownProvider dropdownId='paypal-actions-menu' dropdownName='paypal-actions-menu'>
                            <StanDropdownMenu
                                id='paypal-actions-menu'
                                name='paypal-actions-menu'
                                options={[
                                    {
                                        label: 'Disconnect Account',
                                        value: 'disconnect',
                                    },
                                ]}
                                placement='bottom-start'
                                onSelect={handleMenuSelect}
                                currentValue={null}
                                minWidth={180}
                                disabled={loading || !paypal.integrationId}
                            >
                                <IcMoreVertical />
                            </StanDropdownMenu>
                        </DropdownProvider>
                    </div>
                </div>
            </div>

            {paypal.isRestricted && (
                <p className='text-caption-2-bold text-negative-fg-negative-bold'>
                    {paypal.isRestrictedDueToEmail ? (
                        'To accept payments, please verify your PayPal email address.'
                    ) : (
                        <>
                            Your account connection is not complete.{' '}
                            <button className='font-bold hover:underline' onClick={onHelpDoc}>
                                Learn more.
                            </button>
                        </>
                    )}
                </p>
            )}
        </div>
    );
};
